import React from 'react';
import styled from 'styled-components';
import SquareRoot from '@svg/square_root';
import httpChangelog from './http-client.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Tooltip } from '@common/Tooltip';
import { Badge, Chip, Stack } from '@mui/material';
import { SecurityOutlined } from '@mui/icons-material';
import { isConnectable, isValidConnection } from '@common/Node/utils';
import { useTranslate } from '@common/hooks/useTranslate';
import { useNodeError } from '@common/hooks/useNodeError';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

import {
  Anchor,
  HTTPNodeState,
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
} from '../../';

import {
  InputAnchors,
  NodeAnchorsContainer,
  NodeContainer,
  NodeErrorToolbar,
  NodeHeader,
  OutputAnchors,
} from '@common/Node';

export const config: NodeConfig = {
  version: '1.1',
  title: 'HTTP Client',
  identifier: 'HTTPClient',
  command_id: 'http.client',
  background: '#9a90e9',
  icon: 'globe',
  changelog: httpChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'http.client.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'http.client.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Finished',
      id: 'http.client.finished',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'http.client.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: HTTPNodeState = {
  method: 'get',
  url: {
    value: '',
    type: NodeInputType.Expression,
  },
  query_parameters: [],
  header_parameters: [],
  body_format: 'form',
  body_parameters: [],
  body_json: { type: NodeInputType.Expression, value: `{\n   "example": "value"\n}` },
  auth: {
    enabled: false,
    type: { value: 'bearer', type: NodeInputType.Option },
    name: { value: 'Authorization', type: NodeInputType.Text },
    value: { value: '', type: NodeInputType.Text },
    secretUsername: '',
    secretHeader: '',
    secretUsage: 'bearer',
    secretName: '',
    secret: true,
  },
  on_error: 'stop',
  parse_response_as: 'json',
};

export const fields: NodeField[] = [
  {
    tab: '1',
    id: 'method',
    type: NodeFieldType.Option,
    label: 'Method',
    description: 'The HTTP method to use for this request.',
    options: [
      { label: 'GET', value: 'get' },
      { label: 'POST', value: 'post' },
      { label: 'PUT', value: 'put' },
      { label: 'PATCH', value: 'patch' },
      { label: 'DELETE', value: 'delete' },
    ],
  },
  {
    tab: '1',
    id: 'url',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'URL',
    placeholder: 'https://example.com/your/path',
    description: 'The URL to send the request to.',
  },
  {
    tab: '2',
    id: 'query_parameters',
    type: NodeFieldType.RequestParameterExpression,
    label: 'Query Parameters',
    description: 'Add query parameters to be sent with this request.',
  },
  {
    tab: '2',
    id: 'header_parameters',
    type: NodeFieldType.RequestParameterExpression,
    label: 'Header Parameters',
    description: 'Add header parameters to to be sent with this request.',
  },
  {
    tab: '2',
    id: 'body_format',
    type: NodeFieldType.Toggle,
    label: 'Body Format',
    options: [
      { label: 'Form', value: 'form' },
      { label: 'JSON', value: 'json' },
    ],
    description:
      'Specify how to format the body of the request. The "FORM" input type is used for simple values, and the "JSON" input type is used for complex data structures like a nested object.',
  },
  {
    tab: '2',
    id: 'body_parameters',
    type: NodeFieldType.RequestParameterExpression,
    label: 'Body Parameters',
    description: 'Add body parameters to be sent with this request.',
  },
  {
    tab: '2',
    id: 'body_json',
    type: NodeFieldType.InputExpression,
    label: 'Body Parameters',
    description: 'Define the output schema for your request body. The output must be a valid JSON object.',
  },
  {
    tab: '3',
    id: 'auth',
    type: NodeFieldType.RequestAuth,
    label: 'Authentication',
    description: 'Authenticate the HTTP request with credentials.',
  },
  {
    tab: '4',
    id: 'on_error',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    label: 'On Error',
    options: [
      {
        label: 'Stop Workflow',
        value: 'stop',
      },
      {
        label: 'Continue',
        value: 'continue',
      },
    ],
    description: 'Describe how you want your workflow to react to an error response.',
  },
  {
    tab: '4',
    id: 'parse_response_as',
    type: NodeFieldType.Option,
    label: 'Parse Response As',
    options: [
      {
        label: 'Raw',
        value: 'raw',
      },
      {
        label: 'JSON',
        value: 'json',
      },
    ],
    description: 'Define how to parse the response.',
  },
];

export const output: NodeOutput = {
  default: {
    http: {
      client: 'unknown',
    },
  },
  dynamic: null,
};

const Extensions = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.text.subtext};

  padding: 0.75rem 1rem;
  border-radius: 7px 7px 0 0;
  font-family: 'Inter';

  border-bottom: 1px solid ${({ theme }) => theme.line.node};

  & .MuiBadge-badge {
    color: white;
    height: 12px;
    min-width: 12px !important;
    max-width: 12px !important;
    font-size: 8px;
    padding: 0;
    left: -10 !important;
    margin-right: 0.25rem;
    margin-top: 0.15rem;
  }

  & .MuiChip-root {
    padding: 8px;
    padding-right: 4px;
  }

  & .MuiChip-icon {
    margin-left: 0 !important;
  }
`;

const RequestInfo = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;

  padding: 0.75rem 1rem;
  border-radius: 7px 7px 0 0;

  p {
    color: ${({ theme }) => theme.text.subtext};
    font-family: 'Inter';
    font-size: 10px;
  }

  border-bottom: 1px solid ${({ theme }) => theme.line.node};
`;

export const HTTPClient: React.FC<NodeType> = ({ id, selected, data }) => {
  const { background, anchors, icon } = config;
  const { isInfiniteLoop } = useTranslate();

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const edges = useSelector((state: State) => state.tabs.active.edges);
  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'http.client') return;

  const connections = command.connections || [];
  const nodeState: HTTPNodeState = command.state;

  const url = nodeState.url.value;
  const method = nodeState.method;
  const queryParameters = nodeState.query_parameters;
  const headerParameters = nodeState.header_parameters;
  const bodyParameters = nodeState.body_parameters;

  const authEnabled = nodeState.auth.enabled;
  const authType = nodeState.auth.type;
  const authValue = nodeState.auth.value;
  const authName = nodeState.auth.name;
  const authSecret = nodeState.auth.secret;
  const secretUsage = nodeState.auth.secretUsage;
  const secretName = nodeState.auth.secretName;

  const body_methods = ['post', 'put', 'patch'];

  const TTHeaderParameters = [
    'Header Parameters\n',
    headerParameters.map((param) => `- ${param.name}: ${param.value.value || 'null'}`).join('\n'),
  ].join('\n');

  const TTQueryParameters = [
    'Query Parameters\n',
    queryParameters.map((param) => `- ${param.name}: ${param.value.value || 'null'}`).join('\n'),
  ].join('\n');

  const TTBodyParameters = [
    'Body Parameters\n',
    bodyParameters
      .filter((param) => param.name)
      .map((param) => (param.name ? `- ${param.name}: ${param.value.value || 'null'}` : null))
      .join('\n'),
  ].join('\n');

  const extensions = [
    {
      name: 'Query',
      badgeValue: queryParameters.filter((param) => param.name).length,
      tooltip: TTQueryParameters,
      visible: queryParameters.filter((param) => param.name).length,
    },
    {
      name: 'Header',
      badgeValue: headerParameters.filter((param) => param.name).length,
      tooltip: TTHeaderParameters,
      visible: headerParameters.filter((param) => param.name).length,
    },
    {
      name: 'Body',
      badgeValue: bodyParameters.filter((param) => param.name).length,
      tooltip: TTBodyParameters,
      visible: body_methods.includes(method) && bodyParameters.filter((param) => param.name).length,
    },
  ];

  const extensionsEnabled = authEnabled || extensions.some((item) => item.visible);

  const output = command.output.dynamic;
  const sources = anchors.filter((anchor) => anchor.type === 'source');
  const targets = anchors.filter((anchor) => anchor.type === 'target');

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={`${method.toUpperCase()}`} icon={icon} background={background} status={nodeStatus} />
      {url && (
        <RequestInfo title={url}>
          <p>{url.length > 39 ? `${url.substring(0, 39)}...` : url}</p>
        </RequestInfo>
      )}
      {extensionsEnabled && (
        <Extensions>
          {authEnabled && (
            <Tooltip
              sx={{ maxWidth: '500px' }}
              title={[
                `Authentication\n`,
                !authSecret && `Type: ${authType.value}`,
                secretName && !authSecret && `Name: ${authName.value}`,
                !authSecret && `Value: ${authValue.value}\n`,
                secretName && authSecret && `Secret Name: ${secretName}`,
                authSecret && `Secret Usage: ${secretUsage}`,
              ]
                .filter(Boolean)
                .join('\n')}
              placement='top'
              arrow
              key={id}
            >
              <Chip
                onClick={() => null}
                label='Auth'
                color='primary'
                icon={<SecurityOutlined sx={{ fontSize: '14px !important' }} />}
                variant='outlined'
                size='small'
                sx={{ fontSize: '9px' }}
              />
            </Tooltip>
          )}
          {extensions.map(({ name, badgeValue, tooltip, visible }) =>
            visible ? (
              <Tooltip sx={{ maxWidth: '500px' }} key={name} title={tooltip} placement='top' arrow>
                <Badge badgeContent={badgeValue} color='primary'>
                  <Chip
                    color='primary'
                    onClick={() => null}
                    label={name}
                    variant='outlined'
                    icon={<SquareRoot height={13} />}
                    size='small'
                    sx={{ fontSize: '9px' }}
                  />
                </Badge>
              </Tooltip>
            ) : null
          )}
        </Extensions>
      )}
      <NodeAnchorsContainer>
        <InputAnchors hasInputs={Boolean(targets.length && sources.length)}>
          {targets.map((target) => (
            <Anchor
              key={`${target.id}${id}`}
              id={id}
              data={data}
              anchor={target}
              isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
              isConnectable={isConnectable(target, connections)}
            />
          ))}
        </InputAnchors>
        <OutputAnchors>
          {sources.map((source) => (
            <Anchor
              key={`${source.id}${id}`}
              id={id}
              data={data}
              anchor={source}
              dataDisabled={!output}
              isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
              isConnectable={isConnectable(source, connections)}
            />
          ))}
        </OutputAnchors>
      </NodeAnchorsContainer>
    </NodeContainer>
  );
};

export default { config, state, fields, output };
